// MARK://dhk8/guest/components/ActiveOrderCard
// Acceso permanente al pedido en curso desde el menú.
//
// 🔴 El hueco que esto cierra (reportado 2026-08-21): el comensal manda su
// pedido, ve el rastreador, le da "atrás" al navegador y aterriza en el menú
// SIN NINGUNA forma de volver a su orden ni de ver su comanda. La única ruta al
// rastreador era la navegación automática justo después de enviar; perdida ésa,
// el pedido quedaba invisible aunque siguiera vivo en la mesa.
//
// La cuenta es de la MESA (sesión compartida): `getMyTableOrder` resuelve el
// tenant/mesa desde los claims del token, así que también funciona para quien se
// unió con el código. Si la mesa no tiene cuenta abierta, no se pinta nada.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getMyTableOrder } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { formatMoney } from '../lib/money.js'

interface OrdenViva {
  id: string
  status: string
  totalCents: number
  items?: { status: string }[]
}

/**
 * Fase de cocina derivada de los ITEMS — espejo de `kitchenPhase()` en
 * OrderTracker. `order.status` NO sirve para esto: se queda en SENT_TO_KITCHEN
 * aunque la cocina ya haya marcado listo y entregado, y el banner decía
 * "En preparación" con el plato ya en la mesa.
 */
function faseCocina(items: { status: string }[]): 'preparing' | 'ready' | 'delivered' | null {
  const vivos = items.filter((it) => it.status !== 'CANCELLED')
  if (vivos.length === 0) return null
  if (vivos.every((it) => it.status === 'DELIVERED')) return 'delivered'
  if (vivos.every((it) => it.status === 'READY' || it.status === 'DELIVERED')) return 'ready'
  if (vivos.some((it) => ['SENT', 'PREPARING', 'READY'].includes(it.status))) return 'preparing'
  return null
}

/** Etiqueta amable. `null` = no hay nada vivo que mostrar. */
function etiqueta(o: OrdenViva): string | null {
  if (o.status === 'CANCELLED' || o.status === 'REJECTED' || o.status === 'CLOSED') return null
  if (o.status === 'PENDING_ACCEPTANCE') return 'Esperando confirmación'
  switch (faseCocina(o.items ?? [])) {
    case 'delivered': return 'Entregado — puedes pagar'
    case 'ready': return 'Listo'
    case 'preparing': return 'En preparación'
    default: return 'En curso'
  }
}

export function ActiveOrderCard({ slug }: { slug: string | undefined }) {
  const session = useSessionStore((s) => s.session)
  const [orden, setOrden] = useState<OrdenViva | null>(null)

  useEffect(() => {
    if (!session) return
    let vivo = true
    const cargar = async (): Promise<void> => {
      const r = await getMyTableOrder(session.token)
      if (!vivo) return
      // NOT_FOUND = la mesa no tiene cuenta abierta. No es un error que mostrar.
      if (!r.ok) { setOrden(null); return }
      const o = r.data as unknown as OrdenViva
      setOrden(etiqueta(o) === null ? null : o)
    }
    void cargar()
    // Refresco modesto: el rastreador ya sondea fino; aquí basta con no quedar rancio.
    const iv = setInterval(() => { void cargar() }, 15_000)
    const onFocus = (): void => { void cargar() }
    window.addEventListener('focus', onFocus)
    return () => { vivo = false; clearInterval(iv); window.removeEventListener('focus', onFocus) }
  }, [session])

  if (!session || !orden) return <></>
  const label = etiqueta(orden)
  if (label === null) return <></>

  return (
    <Link to={`/r/${slug}/order/${orden.id}`} className="card" style={{ display: 'block', textDecoration: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className="join-eyebrow">Tu pedido está en curso</div>
          <strong style={{ display: 'block', marginTop: 2 }}>{label}</strong>
        </div>
        <div style={{ textAlign: 'right' }}>
          <strong>{formatMoney(orden.totalCents)}</strong>
          <div className="muted" style={{ fontSize: 13 }}>Ver mi pedido →</div>
        </div>
      </div>
    </Link>
  )
}
